export type StorageOption = "localStorage" | "sessionStorage";

/**
 * Our storage class provides an interface that resolves to localStorage or sessionStorage
 * or falls back to a map if the chosen type is not available
 */
export class Storage {
  // If the storage type is not available, we fall back to a Map
  #storageFallback = new Map<string, string>();
  #type: StorageOption;
  #storageAvailable: boolean;

  constructor(type: StorageOption = "localStorage") {
    this.#type = type;
    this.#storageAvailable = storageAvailable(this.#type);
  }

  setItem(key: string, value: string): void {
    if (this.#storageAvailable) {
      window[this.#type].setItem(key, value);
    } else {
      this.#storageFallback.set(key, value);
    }
  }

  getItem(key: string): string | null {
    if (this.#storageAvailable) {
      return window[this.#type].getItem(key);
    } else {
      return this.#storageFallback.get(key) || null;
    }
  }

  removeItem(key: string): void {
    if (this.#storageAvailable) {
      window[this.#type].removeItem(key);
    } else {
      this.#storageFallback.delete(key);
    }
  }
}

export const localStorage = new Storage();

function storageAvailable(type: "localStorage" | "sessionStorage"): boolean {
  let storage;
  try {
    storage = window[type];
    const x = "__storage_test__";
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      e.name === "QuotaExceededError" &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage !== undefined &&
      storage.length !== 0
    );
  }
}
